import { getGoalTrackerData, getGoalTrackerDataMock, parseErrorStatus } from "../services/appRequest";
import { socketRequest } from "../services/socketRequest";
import { getClientLoggerInstance } from "../helpers/clientLogger";
import { customElementName } from "../defineCustomElement";
import { isNullOrUndefined } from "../helpers/helpers";

import { playConfetti } from "../helpers/play-confetti";
import { isInViewport } from "../helpers/helpers";
let timeOutIdhandleInView;
class GoalTracker {
    #goalData;
    #handlers = {
        "error": [],
        "dataReady": [],
        "complete": [],
        "inProgress": [],
        "render": [],
        "inView": []
    };
    // #pollInstance;
    #socketInstance;
    #isAnimationComplete = false;
    #isInView = null;
    isRendered = false;

    constructor(goalRef, opts = {}) {
        this.goalRef = goalRef;
        this.ready = false;
        this.isRendered = false;
        this.isComplete = false;
        window.GoalTracker.goalTrackers[this.goalRef] = this;
        this.wvusLogger = this.#initLogger(goalRef);
        this.#initSocketConnection(goalRef);
    }

    #initLogger(goalRef) {
        if (!this.wvusLogger) {
           return getClientLoggerInstance({goalRef});
        }

        return this.wvusLogger;
    }

    #initSocketConnection(goalRef) {
        this.#socketInstance = socketRequest({ asyncCall: getGoalTrackerData, goalRef, errorLogger: this.errorCapture.bind(this)});
        this.#socketInstance.subscribe(this.#goalDataSubscription.bind(this));
        this.#socketInstance.start(goalRef);
    }

    #initOnScrollView() {
        this.#isInView = isInViewport(this.goalTrackerElement);

        const inViewHandler = function() {
            const firstInView = isInViewport(this.goalTrackerElement);
            const inViewTimeoutId = setTimeout(() => {
                const isInView = isInViewport(this.goalTrackerElement);
                if ((firstInView === isInView) && (isInView !== this.#isInView)) {
                    this.#isInView = isInView;
                    this.handleInOffView(isInView);
                } 
                clearTimeout(inViewTimeoutId);
            }, 500);
        }.bind(this);

        window.addEventListener('scroll', inViewHandler, false);
    }

    #handleFnc(handlerType) {
        return (...args) => {
            if (!this.#handlers[handlerType]) {
                return;
            }

            const params = [...args, this];
            for (const handler of this.#handlers[handlerType]) {
                try {
                    handler(...params);
                } catch(err) {
                    this.errorCapture(err);
                }
            }
        };
    }

    #goalDataSubscription(error, data) {
        if (error) {
            // Handle Error
            this.errorCapture(error);
            return;
        }

        this.#goalData = data;

        const currentProgressState = this.getProgressState();

        if (!this.ready) {
            this.ready = true;
            this.handleOnDataReady(data);
        }

        if (currentProgressState === "complete" && !this.isComplete) {
            // Trigger On Complete Handler
            this.handleOnComplete(data);
            this.isComplete = true;
        } else if (currentProgressState === "inProgress") {
            // Trigger On In Progress Handler
            this.handleOnInProgress(data);
        }
    }

    async #handleInView() {
        clearTimeout(timeOutIdhandleInView);
        
        if (!this.goalTrackerElement) {
            this.handleInOffView(false);
        } else {
            const firstInView = isInViewport(this.goalTrackerElement);
            timeOutIdhandleInView = setTimeout(() => {
                const isInView = isInViewport(this.goalTrackerElement);

                if (isInView && isInView === firstInView) {
                    this.handleInOffView(isInView);
                } else if (!isInView && !firstInView) {
                    this.handleInOffView(false);
                }
            }, 500);
        }
    }

    #initAnimation() {
        this.onComplete((data) => {
            const { displayType } = data;
            
            if (!this.#isAnimationComplete && displayType === "bubble") {
                this.#isAnimationComplete = true;
                let animationFire = false;
                const removeFnc = this.onInView(() => {
                    if (!animationFire) {
                        animationFire = true;
                        playConfetti(this.goalTrackerElement);
                    }
                    removeFnc();
                });
            }
        });
    }

    logger(...args) {
        return this.wvusLogger.log(...args);
    }

    addGoalTrackerElement(goalTrackerElement) {
        this.goalTrackerElement = goalTrackerElement;
        this.#initOnScrollView();
        this.#initAnimation();
    }

    addHeartAnimationElement(animationWrapperElement) {
        this.heartAnimationInstance.initElement(animationWrapperElement);
        this.onComplete(() => {
            this.heartAnimationInstance.play();
        });
    }

    render(parentSelector) {
        if (!this.goalTrackerElement) {
            this.parentSelector = parentSelector;
            this.parentElement = document.querySelector(this.parentSelector);
            this.parentElement.insertAdjacentHTML("beforeend", `<${customElementName} goalRef="${this.goalRef}"></${customElementName}>`);
          } else {
            console.warn("Element already exist");
            this.logger("Element already exist", "warning");
        } 
    }

    stopPoll() {
        this.#socketInstance.stop();
    }

    subscribeData(fnc) {
        return this.#socketInstance.subscribe((...args) => fnc(...args));
    }

    async getData() {
        return new Promise((resolve, reject) => {
            if (this.#goalData) {
                resolve(this.#goalData);
            } else {
                getGoalTrackerData(this.goalRef).then((data) => {
                    this.#goalData = data;
                    this.#goalDataSubscription(null, data);
                    resolve(data);
                }).catch((err) => {
                    this.#goalDataSubscription(err);
                    reject(err);
                });
            }
        });
    }

    getProps() {
        return {
            goalDataSubscribe: this.#socketInstance.subscribe,
            onInView: this.onInView.bind(this),
            onComplete: this.onComplete.bind(this),
            errorCapture: this.errorCapture.bind(this),
            handleOnRender: this.handleOnRender.bind(this),
        };
    }

    getProgressState() {        
        const { currentGoalState, goalTarget } = this.#goalData || {};

        if (!this.#goalData || isNullOrUndefined(this.#goalData?.currentGoalState) || isNullOrUndefined(this.#goalData?.goalTarget)) {
            return "pending";
        }

        // Determine Progress State Here
        const complete = currentGoalState >= goalTarget;

        const progressState =  complete ? "complete" : "inProgress";

        return progressState;
    }

    async fetchGoalData(freshRequest = false) {
        if (!freshRequest && this.#goalData) {
            return this.#goalData;
        }

        try {
            const data = await getGoalTrackerData(this.goalRef);
            return data;
        } catch (err) {
            this.errorCapture(err);
            return Promise.reject(err);
        }
    }

    addHandler(handlerType, fnc) {
        this.#handlers[handlerType].push(fnc);
        return () => this.remove(handlerType, fnc);
    }

    remove(handlerType, fnc) {
        if (this.#handlers[handlerType]) {
            this.#handlers[handlerType] = this.#handlers[handlerType].filter((handler) => fnc !== handler);
        }
    }

    onError(fnc) {
        return this.addHandler("error", fnc);
    }

    onInView(fnc) {
        const removeFnc = this.addHandler("inView", fnc);
        this.#handleInView();
        return removeFnc;
    }

    onOffView(fnc) {
        const removeFnc = this.addHandler("offView", fnc);
        this.#handleInView();
        return removeFnc;
    }

    onDataReceived(fnc) {
        return this.#socketInstance.subscribe((error, data) => {
            if (!error) {
                fnc(data);
            }
        });
    }

    onDataReady(fnc) {
        if (this.ready) {
            fnc(this.#goalData);
        }

        return this.addHandler("dataReady", fnc);
    }

    onInProgress(fnc) {
        const progressState = this.getProgressState();

        if (progressState === "inProgress") {
            try {
                fnc(this.#goalData, this);
            } catch (err) {
                this.errorCapture(err);
            }
        }

        return this.addHandler("inProgress", fnc);
    }

    onComplete(fnc) {
        const progressState = this.getProgressState();
        if (progressState === "complete") {
            try {
                fnc(this.#goalData, this);
            } catch (err) {
                this.errorCapture(err);
            }
        }

        return this.addHandler("complete", fnc);
    }

    onRender(fnc) {
        if (this.isRendered) {
            fnc(this.#goalData);
        }

        return this.addHandler("render", fnc);
    }

    handleInOffView(inView) {
        if (inView) {
            this.#handleFnc("inView")(this.#goalData);
        } else {
            this.#handleFnc("offView")(this.#goalData);
        }
    }

    handleOnDataReady(data) {
        this.#handleFnc("dataReady")(data);
    }

    handleOnInProgress(data) {
        this.#handleFnc("inProgress")(data);
    }

    handleOnComplete(data) {
        this.#handleFnc("complete")(data);
    }

    handleOnRender() {
        if (!this.isRendered) {
            this.isRendered = true;
            this.#handleFnc("render")(this.#goalData);
        }
    }

    errorParse(error) {
        if(typeof error === "string") {
            return error;
        } else if (typeof error.message === "string") {
            return error.message;
        } else {
            return "unknown error detected";
        }
    }

    errorCapture(error) {
        this.logger(this.errorParse(error), "error");
        this.#handleFnc("error")(error);
    }

    setContent(opts = {}) {

        if (!Object.keys(opts).length) {
            return;
        }

        if (opts.progressState && opts.parentSelector && opts.content) {
            const renderContent = () => {
                
                const parentElement = document.querySelector(opts.parentSelector);

                if (parentElement) {
                    parentElement.innerHTML = opts.content;
                } else {
                    console.warn("GoalTracker.renderContent:", "Parent element not found");
                }
            };

            if (opts.progressState === "inProgress") {
                this.onInProgress((data) => {
                    renderContent();
                });

            } else if (opts.progressState === "complete") {
                this.onComplete((data) => {
                    renderContent();
                });
            }

        } else {
            console.warn("GoalTracker.renderContent:", "Invalid options");
        }

    }

}

window.GoalTracker = GoalTracker;

// Static Reference
window.GoalTracker.goalTrackers = window.GoalTracker.goalTrackers || {};

window.GoalTracker.getInstance = (goalRef) => {
    if (window.GoalTracker.goalTrackers[goalRef]) {
        return window.GoalTracker.goalTrackers[goalRef];
    } else {
        return new window.GoalTracker(goalRef);
    }
};

export default window.GoalTracker;
